import React, { useState, useEffect } from 'react';
import ticketsService, { Ticket, TicketFilters } from '@/services/ticketsService';
import LoadingSpinner from '@/components/LoadingSpinner';
import TicketActions from '@/components/tickets/TicketActions';
import ticketActionsService from '@/services/ticketActionsService';
import EmailModal from '@/components/tickets/EmailModal';
import { convertTicketToWeighingTicketData, getTicketReference } from '@/utils/ticketConversion';

const TicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [filters, setFilters] = useState<TicketFilters>({
    search: '',
    statut: ''
  });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    recipients: '',
    subject: '',
    message: '',
    cc: '',
    bcc: ''
  });

  // Charger les tickets au montage du composant
  useEffect(() => {
    loadTickets();
  }, []);

  // Recharger les tickets quand les filtres changent
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadTickets();
    }, 500); // Debounce de 500ms

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await ticketsService.getTickets(filters);
      
      // Vérifier la structure de la réponse
      if (response && response.data) {
        setTickets(response.data);
      } else if (Array.isArray(response)) {
        setTickets(response);
      } else {
        console.error('Structure de réponse inattendue:', response);
        setTickets([]);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des tickets:', err);
      setError(err.message || 'Erreur lors du chargement des tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof TicketFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const selectTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
  };

  const getTypeLabel = (statut: string) => {
    switch (statut) {
      case 'COMPLETEE':
        return { label: 'Pesée validée', icon: 'fas fa-check', color: 'bg-green-100 text-green-800' };
      case 'REFOULEE':
        return { label: 'Refoulement', icon: 'fas fa-times', color: 'bg-red-100 text-red-800' };
      default:
        return { label: 'Inconnu', icon: 'fas fa-question', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'COMPLETEE':
        return { label: 'Complétée', color: 'text-green-600 font-medium' };
      case 'REFOULEE':
        return { label: 'Refoulée', color: 'text-red-600 font-medium' };
      default:
        return { label: 'Inconnu', color: 'text-gray-600 font-medium' };
    }
  };

  const handlePrintTicket = async (ticket: Ticket) => {
    try {
      await ticketActionsService.printTicket(ticket, {
        onSuccess: () => console.log('✅ Impression réussie'),
        onError: (error) => setError(error)
      });
    } catch (err: any) {
      console.error('Erreur lors de l\'impression:', err);
      setError(err.message || 'Erreur lors de l\'impression du ticket');
    }
  };

  const handleExportPDF = async (ticket: Ticket) => {
    try {
      await ticketActionsService.exportTicketPDF(ticket, {
        onSuccess: () => console.log('✅ Export PDF réussi'),
        onError: (error) => setError(error)
      });
    } catch (err: any) {
      console.error('Erreur lors de l\'export PDF:', err);
      setError(err.message || 'Erreur lors de l\'export PDF du ticket');
    }
  };

  const handleSendEmail = async (ticket: Ticket) => {
    if (!ticket) return;
    
    // Convertir le ticket en WeighingTicketData pour le modal
    const ticketData = convertTicketToWeighingTicketData(ticket);

    // Pré-remplir les données d'email
    setEmailData({
      recipients: '',
      subject: `Ticket de Pesée - ${ticketData.code}`,
      message: `Bonjour,

Veuillez trouver ci-joint le ticket de pesée ${ticketData.code} pour la pesée effectuée le ${ticketData.weighingDate}.

Détails de la pesée :
- Référence : ${ticketData.code}
- Produit : ${ticketData.product}
- Véhicule : ${ticketData.vehicle}
- Poids brut : ${ticketData.grossWeight.toLocaleString()} kg
- Poids net : ${ticketData.netWeight.toLocaleString()} kg
- Fournisseur : ${ticketData.supplierIdentity || 'N/A'}

Le ticket PDF est joint à cet email.

Cordialement,
Service de Pesée - Conseil du Coton et de l'Anacarde`,
      cc: '',
      bcc: ''
    });
    
    setShowEmailModal(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-between mb-6 p-6">
        <div className="flex items-center">
          <button className="p-2 text-gray-400 hover:text-gray-600 mr-4">
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1 className="text-xl font-medium text-gray-900">Gestion des Tickets & Refoulements</h1>
        </div>
      </div>

      {error && (
        <div className="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des Tickets */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200 bg-gray-100 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-900">Liste des Tickets</h3>
                <div className="flex space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher par numéro"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                    />
                    <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                  <select
                    value={filters.statut}
                    onChange={(e) => handleFilterChange('statut', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="">Tous statuts</option>
                    <option value="COMPLETEE">Pesée validée</option>
                    <option value="REFOULEE">Refoulement</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Ticket</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tickets && tickets.length > 0 ? tickets.map((ticket) => {
                    const typeInfo = getTypeLabel(ticket.statut);
                    const statusInfo = getStatusLabel(ticket.statut);
                    
                    return (
                      <tr 
                        key={ticket.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => selectTicket(ticket)}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {getTicketReference(ticket)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{formatDate(ticket.date_heure)}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2 py-1 rounded text-xs flex items-center w-fit ${typeInfo.color}`}>
                            <i className={`${typeInfo.icon} mr-1`}></i>{typeInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={statusInfo.color}>{statusInfo.label}</span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button 
                            className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrintTicket(ticket);
                            }}
                          >
                            <i className="fas fa-print mr-1"></i>Imprimer
                          </button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        {loading ? 'Chargement...' : 'Aucun ticket trouvé'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Détail Ticket */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center mb-4">
              <i className="fas fa-ticket-alt text-orange-600 mr-2"></i>
              <h3 className="font-medium text-gray-900">Détail Ticket</h3>
            </div>

            {selectedTicket ? (
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-600">N°:</span>
                  <p className="font-medium">{getTicketReference(selectedTicket)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Date:</span>
                  <p className="font-medium">{formatDate(selectedTicket.date_heure)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Type:</span>
                  <span className={`px-2 py-1 rounded text-xs flex items-center w-fit mt-1 ${getTypeLabel(selectedTicket.statut).color}`}>
                    <i className={`${getTypeLabel(selectedTicket.statut).icon} mr-1`}></i>
                    {getTypeLabel(selectedTicket.statut).label}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Statut:</span>
                  <p className={`font-medium ${getStatusLabel(selectedTicket.statut).color}`}>
                    {getStatusLabel(selectedTicket.statut).label}
                  </p>
                </div>

                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Informations Pesée</h4>
                  <div className="text-sm space-y-1">
                    {selectedTicket.camion_immatriculation && (
                      <p><span className="text-gray-600">Camion:</span> {selectedTicket.camion_immatriculation}</p>
                    )}
                    {selectedTicket.chauffeur_nom && (
                      <p><span className="text-gray-600">Chauffeur:</span> {selectedTicket.chauffeur_nom}</p>
                    )}
                    {selectedTicket.produit_nom && (
                      <p><span className="text-gray-600">Produit:</span> {selectedTicket.produit_nom}</p>
                    )}
                    {selectedTicket.poids_net && (
                      <p><span className="text-gray-600">Poids net:</span> {selectedTicket.poids_net} kg</p>
                    )}
                    {selectedTicket.poids_brut && (
                      <p><span className="text-gray-600">Poids brut:</span> {selectedTicket.poids_brut} kg</p>
                    )}
                    {selectedTicket.poids_tare && (
                      <p><span className="text-gray-600">Poids tare:</span> {selectedTicket.poids_tare} kg</p>
                    )}
                    {selectedTicket.observations && (
                      <p><span className="text-gray-600">Observations:</span> {selectedTicket.observations}</p>
                    )}
                    {selectedTicket.statut === 'COMPLETEE' && (
                      <p><span className="text-gray-600">Qualité:</span> <span className="text-green-600">✓ Conforme</span></p>
                    )}
                    {selectedTicket.statut === 'REFOULEE' && (
                      <p><span className="text-gray-600">Motif refoulement:</span> <span className="text-red-600">{selectedTicket.observations || 'Non spécifié'}</span></p>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Actions disponibles</h4>
                  <div className="space-y-2">
                    {selectedTicket.statut === 'COMPLETEE' ? (
                      <TicketActions
                        ticket={selectedTicket}
                        variant="default"
                        showLabels={true}
                        onPrint={() => handlePrintTicket(selectedTicket)}
                        onExport={() => handleExportPDF(selectedTicket)}
                        onEmail={() => handleSendEmail(selectedTicket)}
                        className="flex-col space-y-2"
                      />
                    ) : selectedTicket.statut === 'REFOULEE' ? (
                      <div className="space-y-2">
                        <button 
                          onClick={() => handlePrintTicket(selectedTicket)}
                          className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
                        >
                          <i className="fas fa-print mr-2"></i>Imprimer Refoulement
                        </button>
                        <button 
                          onClick={() => handleExportPDF(selectedTicket)}
                          className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm transition-colors"
                        >
                          <i className="fas fa-download mr-2"></i>Exporter PDF
                        </button>
                        <button 
                          onClick={() => handleSendEmail(selectedTicket)}
                          className="w-full px-4 py-2 bg-red-400 hover:bg-red-500 text-white rounded-lg text-sm transition-colors"
                        >
                          <i className="fas fa-envelope mr-2"></i>Envoyer Email
                        </button>
                      </div>
                    ) : null}
                    <button 
                      onClick={() => navigator.clipboard.writeText(getTicketReference(selectedTicket))}
                      className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-sm transition-colors"
                    >
                      <i className="fas fa-copy mr-2"></i>Copier Référence
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <i className="fas fa-ticket-alt text-4xl mb-4 text-gray-300"></i>
                <p>Sélectionnez un ticket pour voir les détails</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'envoi d'email */}
      {showEmailModal && selectedTicket && (
        <EmailModal
          ticketData={convertTicketToWeighingTicketData(selectedTicket)}
          emailData={emailData}
          onEmailDataChange={setEmailData}
          onSend={() => setShowEmailModal(false)}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </div>
  );
};

export default TicketsPage;
